import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import { fetchJson, path, type QueryParams } from '@/api/client'
import { keys } from '@/api/keys'
import type { AssetKind, PublishedTier, ReviewStatus } from '@/schema'

/**
 * `/api/assets*`, `/api/categories`, `/api/search` (§5.1). Shapes are the
 * server's own; media URLs inside a card are versioned (`?v=`) and are
 * rendered as-is (§7.4) — the client never builds a media URL by hand.
 */

export type AssetStatus = 'ok' | 'broken' | 'missing' | 'duplicate' | 'too_large'

export interface AssetReview {
  status: ReviewStatus
  rev?: number
  published_rev?: number
  published_tier: PublishedTier | null
  confidence?: number
  lint_errors: number
  lint_warnings: number
  pending_proposals: number
}

export interface AssetMedia {
  thumb: string
  original: string
  poster?: string
  raster?: string
}

export interface AssetCard {
  uid: string
  kind: AssetKind
  group?: string
  category?: string
  world?: string
  slug: string
  name: string
  status: AssetStatus
  enabled: boolean
  hidden: boolean
  width?: number
  height?: number
  duration_s?: number
  parent_uid?: string
  skin?: string
  skin_verified?: boolean
  skin_count?: number
  clip_count?: number
  broken_clip_count?: number
  review: AssetReview
  media: AssetMedia
  keywords: string[]
  updated_at: string
}

export type ClipStatus = 'ok' | 'no_sheet' | 'no_atlas' | 'atlas_mismatch' | 'too_large' | 'missing'

export interface ClipRow {
  id: number
  dir_name: string
  action: string
  facing: 'left' | 'right' | null
  view: 'front' | 'side' | '3q'
  mirrored: boolean
  deprecated: boolean
  mirror_of?: number
  inherited?: boolean
  fps: number
  frame_count: number
  frame: { w: number; h: number }
  status: ClipStatus
  media: { sheet: string; atlas: string; frame0: string }
}

export type AssetDetail = AssetCard & {
  description?: string
  s3_prefix: string
  primary_key?: string
  sidecar: Record<string, unknown> | null
  facing?: string
  real_world_height_cm?: number
  rest_surface?: string
  meta: Record<string, unknown>
  clips?: ClipRow[]
  skins?: AssetCard[]
}

/** Facet counts keyed by value, e.g. `{ nature: 12, city: 4 }`. */
export type FacetCounts = Record<string, number>

export interface AssetFacets {
  category: FacetCounts
  world: FacetCounts
  status: FacetCounts
  review: FacetCounts
  tier: FacetCounts
}

export interface AssetsPage {
  items: AssetCard[]
  total: number
  page: number
  size: number
  facets: AssetFacets
}

export type ReviewFilter = ReviewStatus | 'any'
export type TierFilter = 'auto' | 'reviewed' | 'none' | 'any'
export type StatusFilter = 'ok' | 'broken' | 'missing' | 'all'
export type AssetSort = 'name' | 'updated' | 'confidence'

export interface AssetListFilters {
  kind?: AssetKind
  group?: string
  category?: string
  world?: string
  q?: string
  skin?: 'canonical' | 'all'
  status?: StatusFilter
  enabled?: boolean
  review?: ReviewFilter
  tier?: TierFilter
  has_clip?: string
  sort?: AssetSort
  page?: number
  size?: number
}

/** Default page size for the library's infinite list (server cap is 200). */
export const ASSET_PAGE_SIZE = 100

const EMPTY_FACETS: AssetFacets = { category: {}, world: {}, status: {}, review: {}, tier: {} }

function normaliseFacets(f: Partial<AssetFacets> | undefined): AssetFacets {
  return { ...EMPTY_FACETS, ...(f ?? {}) }
}

export async function listAssets(filters: AssetListFilters = {}, signal?: AbortSignal): Promise<AssetsPage> {
  const page = await fetchJson<Partial<AssetsPage>>('/api/assets', { query: filters as QueryParams, signal })
  return {
    items: page.items ?? [],
    total: page.total ?? page.items?.length ?? 0,
    page: page.page ?? filters.page ?? 1,
    size: page.size ?? filters.size ?? ASSET_PAGE_SIZE,
    facets: normaliseFacets(page.facets),
  }
}

export function getAsset(uid: string, signal?: AbortSignal): Promise<AssetDetail> {
  return fetchJson<AssetDetail>(path`/api/assets/${uid}`, { signal })
}

export interface AssetPatch {
  name?: string
  hidden?: boolean
  parent_uid?: string | null
  skin?: string
  meta?: Record<string, unknown>
}

/** `PATCH /api/assets/{uid}` → writes `asset_overrides`, returns the detail. */
export function patchAsset(uid: string, body: AssetPatch): Promise<AssetDetail> {
  return fetchJson<AssetDetail>(path`/api/assets/${uid}`, { method: 'PATCH', body })
}

export function transferAnnotations(oldUid: string, newUid: string): Promise<{ moved_revisions: number }> {
  return fetchJson(path`/api/assets/${oldUid}/transfer-annotations/${newUid}`, { method: 'POST' })
}

export interface CategoryRow {
  kind: AssetKind
  group?: string
  category: string
  count: number
}

export function listCategories(kind?: AssetKind, signal?: AbortSignal): Promise<{ items: CategoryRow[] }> {
  return fetchJson('/api/categories', { query: { kind }, signal })
}

/** `GET /api/search` — FTS5 bm25 ranked cards for the command palette and quick jump. */
export function searchAssets(q: string, opts: { kind?: AssetKind; limit?: number } = {}, signal?: AbortSignal): Promise<{ items: AssetCard[] }> {
  return fetchJson('/api/search', { query: { q, kind: opts.kind, limit: opts.limit ?? 20 }, signal })
}

/* ------------------------------------------------------------- queries --- */

export const assetsQuery = (filters: AssetListFilters = {}) =>
  queryOptions({
    queryKey: keys.assets.list(filters),
    queryFn: ({ signal }) => listAssets(filters, signal),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  })

/**
 * Infinite variant for the library grid: pages of `ASSET_PAGE_SIZE`, next
 * page while `page * size < total`. `page` in `filters` is ignored.
 */
export const assetsInfiniteQuery = (filters: Omit<AssetListFilters, 'page'> = {}) => {
  const size = filters.size ?? ASSET_PAGE_SIZE
  return infiniteQueryOptions({
    queryKey: keys.assets.list({ ...filters, size, infinite: true }),
    queryFn: ({ pageParam, signal }) => listAssets({ ...filters, size, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.size < last.total ? last.page + 1 : undefined),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  })
}

export const assetQuery = (uid: string) =>
  queryOptions({
    queryKey: keys.assets.detail(uid),
    queryFn: ({ signal }) => getAsset(uid, signal),
    staleTime: 15_000,
  })

export const categoriesQuery = (kind?: AssetKind) =>
  queryOptions({
    queryKey: keys.categories(kind),
    queryFn: ({ signal }) => listCategories(kind, signal),
    staleTime: 60_000,
  })

export const searchQuery = (q: string, opts: { kind?: AssetKind; limit?: number } = {}) =>
  queryOptions({
    queryKey: keys.assets.search(q, opts),
    queryFn: ({ signal }) => searchAssets(q, opts, signal),
    enabled: q.trim().length > 0,
    staleTime: 10_000,
  })

/* ------------------------------------------------------------- helpers --- */

/** Which annotator a card opens in; `null` for audio (no annotation kind, §5.3). */
export function annotationKindOf(kind: AssetKind): 'stage' | 'character' | 'object' | null {
  switch (kind) {
    case 'background':
    case 'live_background':
      return 'stage'
    case 'character':
      return 'character'
    case 'object':
      return 'object'
    default:
      return null
  }
}

/** Short human label for a kind ("Live plate", "Hero"...). */
export const KIND_LABEL: Record<AssetKind, string> = {
  background: 'Background',
  live_background: 'Live plate',
  character: 'Character',
  object: 'Object',
  audio: 'Audio',
}
